/**
 * UNIFIED USER CONTEXT - Single Source of Truth for Authentication
 * Eliminates the multi-context complexity from Supabase era
 * 
 * This is the ONLY user context - all auth should flow through here
 * - JWT-based authentication via Cloudflare Worker
 * - Centralized isPro/isBeta gating
 * - Single /auth/me endpoint for session refresh
 * - No more environment overrides or debug complexity
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getToken, hasToken } from '../utils/tokenStorage';
import { API_CONFIG } from '../config/api';

const UserContext = createContext();

// Use centralized API configuration
const AUTH_CONFIG = {
  workerUrl: API_CONFIG.AUTH.BASE,
  endpoints: {
    me: '/me',
    refresh: '/refresh', 
    logout: '/logout',
    magicStart: '/request-link',
    magicVerify: '/verify'
  }
};

export const UserProvider = ({ children }) => {
  // Core auth state - the single source of truth
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * CENTRALIZED AUTH CHECKER - The Only Way to Determine Auth Status
   * Replaces scattered isPro checks throughout the app
   */
  const authStatus = {
    isAuthenticated: !!user,
    isPro: user?.isPro === true || user?.pro === true || user?.plan === 'pro',
    isBeta: user?.isBeta === true || user?.beta === true || user?.plan === 'beta',
    isLoggedIn: !!user?.email,
    // Development bypass for localhost
    isDevBypass: process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost'
  };

  /**
   * Call Worker's /auth/me endpoint to get current user
   * This is the single source of session state
   * Only makes the request if a token exists to avoid 401 errors
   */
  const fetchCurrentUser = useCallback(async () => {
    try {
      // Skip request if no token exists to prevent 401 errors
      const jwt = getToken();
      if (!jwt) {
        console.log('[UserContext] No token found, skipping /auth/me request');
        setUser(null);
        setError(null);
        return null;
      }
      
      console.log('[UserContext] Token found, making /auth/me request');

      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`
      };

      const response = await fetch(`${AUTH_CONFIG.workerUrl}${AUTH_CONFIG.endpoints.me}`, {
        credentials: 'include', // Include JWT cookies
        headers
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('[UserContext] /auth/me success, user:', userData.user?.email);
        setUser(userData.user);
        setError(null);
        return userData.user;
      } else if (response.status === 401) {
        console.log('[UserContext] /auth/me returned 401, clearing user state');
        // Not authenticated - clear user state
        setUser(null);
        setError(null);
        return null;
      } else {
        const errorText = await response.text();
        console.error('[UserContext] /auth/me failed:', response.status, errorText);
        throw new Error(`Auth check failed: ${response.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('[UserContext] Failed to fetch current user:', err);
      
      // Development fallback - create dev user for localhost
      if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
        const devUser = {
          id: 'dev-user-123',
          email: 'dev@localhost',
          pro: true,
          beta: true,
          plan: 'pro',
          createdAt: new Date().toISOString()
        };
        setUser(devUser);
        setError(null);
        console.log('[UserContext] Using development bypass user');
        return devUser;
      }

      setUser(null);
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Refresh the user session
   */
  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch(`${AUTH_CONFIG.workerUrl}${AUTH_CONFIG.endpoints.refresh}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user);
        return userData.user;
      }

      return null;
    } catch (err) {
      console.error('[UserContext] Session refresh failed:', err);
      return null;
    }
  }, []);

  /**
   * Start magic link authentication flow
   */
  const startMagicAuth = useCallback(async (email, redirectPath = '/my-plan') => {
    try {
      const response = await fetch(`${AUTH_CONFIG.workerUrl}${AUTH_CONFIG.endpoints.magicStart}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email, 
          redirectPath,
          origin: window.location.origin
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send magic link');
      }

      return { success: true, message: result.message };
    } catch (err) {
      console.error('[UserContext] Magic link start failed:', err);
      throw err;
    }
  }, []);

  /**
   * Verify magic link token (simplified)
   */
  const verifyMagicToken = useCallback(async (token, state = '') => {
    const response = await fetch(
      `${AUTH_CONFIG.workerUrl}${AUTH_CONFIG.endpoints.magicVerify}?token=${token}&state=${state}`,
      { credentials: 'include' }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Magic link verification failed');
    }

    const result = await response.json();
    const userData = await fetchCurrentUser();
    return { success: true, user: userData, redirectPath: result.redirectPath };
  }, [fetchCurrentUser]);

  /**
   * Logout - clear all auth state
   */
  const logout = useCallback(async () => {
    try {
      // Call worker logout to clear JWT cookies
      await fetch(`${AUTH_CONFIG.workerUrl}${AUTH_CONFIG.endpoints.logout}`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('[UserContext] Logout request failed:', err);
      // Continue with local logout even if server call fails
    }

    // Clear local state
    setUser(null);
    setError(null);
    
    // Clear any localStorage auth remnants (legacy cleanup)
    ['auth-token', 'user-session', 'magic-auth-state', 'ts_jwt', 'token'].forEach(key => {
      localStorage.removeItem(key);
    });
  }, []);

  /**
   * Initialize auth state on app load
   * Simplified: just check for existing token, let login pages handle magic links
   */
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      
      try {
        // Simple token check - no URL parsing complexity
        await fetchCurrentUser();
      } catch (err) {
        console.error('[UserContext] Auth initialization failed:', err);
        setError(err.message);
      }
      
      setLoading(false);
    };

    initAuth();
  }, [fetchCurrentUser]);

  /**
   * Listen for auth-success events from LoginSuccess page
   */
  useEffect(() => {
    const handleAuthSuccess = async (event) => {
      console.log('[UserContext] Received auth-success event:', event.detail);
      const token = event.detail?.token;
      
      if (token) {
        console.log('[UserContext] Token provided in event, fetching user immediately');
        // Token is passed directly, no need to wait for localStorage
        await fetchCurrentUser();
      } else {
        console.warn('[UserContext] No token in auth-success event, checking localStorage');
        // Fallback to localStorage check with small delay
        setTimeout(async () => {
          await fetchCurrentUser();
        }, 100);
      }
    };

    window.addEventListener('auth-success', handleAuthSuccess);
    return () => window.removeEventListener('auth-success', handleAuthSuccess);
  }, [fetchCurrentUser]);

  /**
   * Auto-refresh session every 30 minutes (skip in development)
   */
  useEffect(() => {
    if (!user || (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost')) return;

    const refreshInterval = setInterval(() => {
      refreshSession();
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(refreshInterval);
  }, [user, refreshSession]);

  const contextValue = {
    // Core auth state
    user,
    loading,
    error,
    
    // Centralized auth status checks
    ...authStatus,
    
    // Auth actions
    startMagicAuth,
    verifyMagicToken,
    logout,
    refreshSession,
    
    // Manual auth check (for debugging)
    checkAuth: fetchCurrentUser
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Hook to access auth context
 * This replaces all the scattered auth imports throughout the app
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

/**
 * HOC for protected routes that require authentication
 */
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading } = useUser();
    
    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!isAuthenticated) {
      return <div>Please log in to access this page.</div>;
    }
    
    return <Component {...props} />;
  };
};

/**
 * HOC for protected routes that require Pro access
 */
export const withPro = (Component) => {
  return function ProComponent(props) {
    const { isPro, loading } = useUser();
    const isDevBypass = process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost';
    
    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!isPro && !isDevBypass) {
      return <div>Pro access required for this feature.</div>;
    }
    
    return <Component {...props} />;
  };
};

export default UserContext;